import type { CSSProperties, ReactNode } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { z } from 'zod'

/**
 * `/authentication/email-confirm` — token-driven email confirmation handoff.
 *
 * The Worker handles the actual token exchange and redirects here with
 * `?state=success|expired|already`. This page only renders the calm result
 * card — same centered-card layout as the other auth surfaces (provided by
 * the parent `/authentication` layout).
 *
 * Per `02-authentication.md` Sub-Surface 11.
 */
const stateSchema = z.enum(['success', 'expired', 'already'])
type ConfirmState = z.infer<typeof stateSchema>

const searchSchema = z.object({
  state: stateSchema.optional(),
})

type SearchParams = z.infer<typeof searchSchema>

export const Route = createFileRoute('/authentication/email-confirm')({
  validateSearch: searchSchema,
  component: EmailConfirm,
})

interface VariantCopy {
  readonly tone: 'success' | 'destructive' | 'warning'
  readonly title: string
  readonly body: string
  readonly cta: {
    readonly label: string
    readonly to: string
  }
}

const VARIANTS: Readonly<Record<ConfirmState, VariantCopy>> = {
  success: {
    tone: 'success',
    title: 'Email confirmed',
    body: 'Your email is verified. You can now use admin actions that require a confirmed mailbox.',
    cta: { label: 'Continue to admin', to: '/admin' },
  },
  expired: {
    tone: 'destructive',
    title: 'Link expired',
    body: "Confirmation links expire after 30 minutes. Request a new one and we'll send it to your inbox.",
    cta: { label: 'Request a new confirmation', to: '/authentication/forgot-password' },
  },
  already: {
    tone: 'warning',
    title: 'Already confirmed',
    body: 'This email was confirmed earlier. No action needed.',
    cta: { label: 'Continue to admin', to: '/admin' },
  },
}

function toneColor(tone: VariantCopy['tone']): string {
  if (tone === 'success') return 'var(--chip-success-fg)'
  if (tone === 'destructive') return 'var(--chip-destructive-fg)'
  return 'var(--chip-warning-fg)'
}

function EmailConfirm(): ReactNode {
  const search = Route.useSearch() as SearchParams
  const state: ConfirmState = search.state ?? 'success'
  const variant = VARIANTS[state]
  const iconStyle = { '--ka-auth-icon-tone': toneColor(variant.tone) } as CSSProperties

  return (
    <div className="ka-auth-center-stack ka-auth-center-stack--gap-16">
      <div aria-hidden="true" className="ka-auth-icon-disk" style={iconStyle}>
        {variant.tone === 'success' ? '✓' : variant.tone === 'destructive' ? '!' : 'i'}
      </div>
      <div>
        <h1 className="ka-auth-status-heading">{variant.title}</h1>
        <p className="ka-auth-status-paragraph">{variant.body}</p>
      </div>
      <Link to={variant.cta.to} className="ka-auth-cta-link">
        {variant.cta.label}
      </Link>
    </div>
  )
}
